import type { ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from './cn';

/*
  StepProgress — честный ход длинной операции (DESIGN.md R32).
  Приём характера: у операции, которая идёт минуты, вместо бесконечной крутилки видно, что
  уже сделано, что идёт сейчас и что впереди. Крутилка не отвечает на единственный важный
  вопрос — «оно вообще движется?». Список шагов отвечает, а заодно показывает, где встало,
  если встало.

  Активный шаг тихо дышит (animate-live-dot), сделанные отмечены галочкой, сорванный — крестом
  и причиной. Порядок шагов не меняется на ходу: строка не переезжает под курсором.
*/

export interface OperationStep {
  id: string;
  label: string;
  status: 'done' | 'active' | 'pending' | 'failed';
  /** Подробность: «40 %», «3 из 8 машин», текст ошибки. */
  note?: string;
}

interface StepProgressProps {
  steps: readonly OperationStep[];
  tone?: 'brand' | 'ai';
  paused?: boolean;
  currentStep?: number;
  statusText?: string;
  renderStep?: (step: OperationStep, row: ReactNode) => ReactNode;
  className?: string;
}

export function StepProgress({
  steps,
  className,
  tone = 'brand',
  paused = false,
  currentStep,
  statusText,
  renderStep,
}: StepProgressProps) {
  const done = steps.filter((s) => s.status === 'done').length;
  const failed = steps.some((s) => s.status === 'failed');

  return (
    <div data-slot="step-progress" className={cn('flex flex-col gap-2', className)}>
      <div data-step-heading="" className="flex items-baseline justify-between text-2xs">
        <span className="font-medium text-fg-muted">
          {statusText ??
            (failed
              ? 'Операция остановлена'
              : done === steps.length
                ? 'Готово'
                : paused
                  ? 'Пауза'
                  : 'Выполняется')}
        </span>
        <span className="text-fg-subtle tabular-nums">
          шаг {Math.min(currentStep ?? done + (failed ? 0 : 1), steps.length)} из {steps.length}
        </span>
      </div>
      <ol className="flex flex-col gap-1.5">
        {steps.map((s) => {
          const row = (
            <>
              <span
                data-step-indicator=""
                className={cn(
                  'grid size-4 shrink-0 place-items-center rounded-full',
                  s.status === 'done' && 'bg-success text-on-success',
                  s.status === 'failed' && 'bg-danger text-on-danger',
                  s.status === 'active' && (tone === 'ai' ? 'bg-ai-soft' : 'bg-brand-soft'),
                  s.status === 'pending' && 'bg-muted',
                )}
              >
                {s.status === 'done' && <Check className="size-3" />}
                {s.status === 'failed' && <X className="size-3" />}
                {s.status === 'active' && (
                  <span
                    className={cn(
                      'size-1.5 rounded-full motion-reduce:animate-none',
                      !paused && 'animate-live-dot',
                      tone === 'ai' ? 'bg-ai' : 'bg-brand',
                    )}
                  />
                )}
              </span>
              <span
                data-step-label=""
                className={cn(
                  'min-w-0 flex-1 truncate',
                  s.status === 'pending' ? 'text-fg-subtle' : 'text-fg',
                  s.status === 'active' && 'font-medium',
                )}
              >
                {s.label}
              </span>
              {s.note && (
                <span
                  className={cn(
                    'shrink-0 tabular-nums',
                    s.status === 'failed' ? 'text-danger-fg' : 'text-fg-subtle',
                  )}
                >
                  {s.note}
                </span>
              )}
            </>
          );
          return (
            <li key={s.id} className={renderStep ? 'min-w-0' : 'flex items-center gap-2 text-xs'}>
              {renderStep ? renderStep(s, row) : row}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
